import React from 'react';
import { Link } from 'react-router-dom';
import Seo, { SITE } from '../../components/Seo/Seo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faMusic, faLocationDot } from '@fortawesome/pro-regular-svg-icons';

import logo from '../../assets/LOGOcolor.png';
import graceHeadshot from '../../assets/GraceHeadshot.jpeg';
import anthonyHeadshot from '../../assets/ApicellaHeadshot2.png';
import './HomeV2.css';

// Restructured for fall 2026 (2026-08-08). This page previously led with summer
// workshops and fetched the live workshop catalog to render seat counts in the
// featured card. Both are gone: the summer content is off the marketing surface
// for the fall push, and with no card left to render, the Firestore read went
// with it rather than being left to run on every landing-page view for output
// nobody sees. /summer itself is still LIVE and reachable by URL — see App.js.
//
// The two CTAs are pointed deliberately and differently:
//   • Fall enrollment → /register. NOT /fall. Anthony unlinked /fall on 08-07
//     because it is information for families who have already enrolled, not an
//     advertisement, and he re-confirmed that hold on 08-08 when this page was
//     wired. /fall stays reachable by URL only.
//   • Free trial → /trial WAS the second CTA (the Aug 9–17 paid push's sole
//     destination). The page and its booking machinery were deleted on
//     2026-09-03 (card PZ9bHlQONFvFRKVgGlH9): trials are offered by hand until
//     Anthony reopens them publicly, so no public surface may advertise one.
//     The featured path card below became fall enrollment at the same time.

const Home = () => {
	return (
		<main className='cyv home-v2'>
			<Seo
				title='Voice Lessons in Burnsville, MN | Color Your Voice'
				description='A creative harbor in Burnsville, MN where kids and teens build confidence through voice, performance, and play. Now enrolling for the 2026–27 school year.'
				canonical={`${SITE}/`}
			/>

			{/* ---- Hero (centered) ---- */}
			<header className='hp-hero'>
				<div className='hp-hero__in'>
					<span className='hp-hero__eyebrow'>
						<span className='dot' />
						<span className='eyebrow'>Fall 2026 · Now enrolling</span>
					</span>
					<img className='hp-hero__word' src={logo} alt='Color Your Voice' />
					<h1 className='brand-heading hp-hero__h1'>
						Helping young artists find their voice.
					</h1>
					<p className='hp-hero__lead'>
						A creative harbor where confidence and artistry grow. Weekly
						one-on-one voice lessons for kids and teens — now enrolling for the
						2026–27 school year.
					</p>
					<div className='hp-hero__cta'>
						<Link className='btn-cyv-teal' to='/register'>
							Enroll for fall →
						</Link>
					</div>
					<span className='hp-hero__loc'>
						<FontAwesomeIcon icon={faLocationDot} /> Inside Headline Dance Center ·
						Burnsville, MN · for kids &amp; teens
					</span>
				</div>
			</header>

			{/* ---- Where would you like to start? (two paths) ---- */}
			<section className='hp-paths'>
				<div className='hp-sec-head'>
					<h2 className='brand-heading'>Where would you like to start?</h2>
				</div>
				<div className='hp-paths__grid'>
					{/* Featured: fall enrollment. Held the free-trial card until 2026-09-03,
					    when /trial was deleted; the hero carries the same CTA, and a stranger's
					    low-commitment first step is now a conversation (/contact, in the side
					    card's page) rather than a self-booked lesson. */}
					<Link
						className='path path--feature'
						style={{ '--accent': 'var(--grad-summer)' }}
						to='/register'
						aria-label='Enroll for the 2026–27 school year'>
						<span className='path__bar' />
						<div className='path__top'>
							<span className='path__icon ic-teal'>
								<FontAwesomeIcon icon={faStar} />
							</span>
							<span className='pill-now'>
								<span className='dot' />
								Now enrolling
							</span>
						</div>
						<h3>Enroll for the 2026–27 school year</h3>
						<p>
							Pick a standing weekly time and your singer keeps it all year — one-on-one
							with a teaching artist who gets to know exactly how they grow, plus studio
							recitals along the way.
						</p>
						<p className='path__fine'>
							Open weekly times are <strong>limited</strong>, and they go quickly.
						</p>
						<span className='path__link'>Pick a time →</span>
					</Link>

					{/* Side: Private lessons */}
					<Link
						className='path path--side'
						style={{ '--accent': 'var(--grad-lessons)' }}
						to='/lessons'
						aria-label='Private lessons'>
						<span className='path__bar' />
						<span className='path__icon ic-orange'>
							<FontAwesomeIcon icon={faMusic} />
						</span>
						<h3>Private lessons</h3>
						<p>
							One-on-one voice lessons across the school year — your own weekly
							time slot, studio recitals, and a teaching artist who gets to know
							exactly how your singer grows.
						</p>
						<span className='path__link'>See how lessons work →</span>
					</Link>
				</div>
			</section>

			{/* ---- Trust strip: faculty narrative + featured testimonial ---- */}
			<section className='hp-trust'>
				<div className='hp-trust__in'>
					<div className='hp-trust__grid'>
						<div className='fac-card'>
							<span className='eyebrow'>Why families choose CYV</span>
							<h3>We built our careers doing this — together.</h3>
							<div className='fac-people'>
								<div className='fac-person'>
									<img src={graceHeadshot} alt='Grace Wall' />
									<span>
										<b>Grace Wall</b>
										<span>Co-owner · teaching artist</span>
									</span>
								</div>
								<div className='fac-person'>
									<img src={anthonyHeadshot} alt='Anthony Apicella' />
									<span>
										<b>Anthony Apicella</b>
										<span>Co-owner · teaching artist</span>
									</span>
								</div>
							</div>
							<p className='fac-card__body'>
								Grace and Anthony spent more than fifteen years making their
								living as performers — in New York, Los Angeles, and on stages
								around the world — often side by side, from the rock musical{' '}
								<em>Rock of Ages</em> to national television for the Special
								Olympics. Grace is an LA Weekly Award-winning actress with credits
								ranging from <em>90210</em> and NBC's <em>The Voice</em> to the
								Hollywood Bowl and Lincoln Center; Anthony has played leading roles
								on the New York stage and toured the globe with symphonies,
								including <em>Disney in Concert</em>. Now they teach the next
								generation together — meeting every young artist exactly where
								they are.
							</p>
							<Link className='morelink' to='/faculty'>
								Meet the faculty →
							</Link>
						</div>

						<figure className='tquote'>
							<span className='tquote__mark'>&ldquo;</span>
							<blockquote className='tquote__text'>
								Color Your Voice is so much more than helping my daughter embrace
								her passion for music and singing - it is about giving her
								confidence and focus to use her VOICE in all the ways she can. Her
								ability to step onto a stage and sing has translated into her
								confidence at school in asking questions, making friends,
								volunteering to help and even - speak at all school assemblies. She
								is stepping into the leader and advocate that she is meant to be -
								and voice lessons with recitals have been a significant part of
								that development.
							</blockquote>
							<figcaption className='tquote__by'>
								<span className='tquote__av'>RR</span>
								<span className='tquote__who'>
									<b>Robin Richards</b>
									<span>Mom of Rowen</span>
								</span>
							</figcaption>
						</figure>
					</div>
				</div>
			</section>

			{/* ---- Fall CTA band (navy) ---- */}
			<section className='hp-cta'>
				<div className='hp-cta__in'>
					<span className='eyebrow'>Fall 2026 · September–May · Burnsville, MN</span>
					<div className='hp-cta__btns'>
						<Link className='btn-cyv-teal' to='/register'>
							Enroll for fall →
						</Link>
					</div>
					{/* Hardcoded marketing chips — deliberately NOT driven by any live
					    data, so a change here is always a hand edit. Swapped from the
					    summer workshop names to the school-year offering 2026-08-08. */}
					<div className='hp-cta__chips'>
						<span>Weekly one-on-one voice</span>
						<span>Studio recitals</span>
						<span>Ages 6 &amp; up</span>
					</div>
				</div>
			</section>
		</main>
	);
};

export default Home;
